import * as d3 from 'd3'
import { ChartData, CPIFrequency, CPITimePeriod } from 'models'
import { addStartEndWeekly, formatPerformanceUnit, groupByWeeks, formatXLabel } from '../../../../library/utils'
import { styledXAxisGroup } from '../chart-components-shared'
import theme from 'theme'
import { resolveThemeColor } from 'theme/config'
import { getActiveThemeMode } from 'theme/themeMode'

interface Props {
  svg: d3.Selection<SVGSVGElement | null, unknown, null, undefined>
  xScaleGroup: d3.ScaleBand<string>
  yScale: d3.ScaleLinear<number, number, never>
  data: ChartData
  filteredData: any[]
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  height: number
  healthUnit: string
}

/*
 * XY-Axis
 * */
export const appendXYAxis = ({
  svg,
  xScaleGroup,
  yScale,
  data,
  filteredData,
  selectedTimePeriod,
  selectedFrequency,
  height,
  healthUnit
}: Props) => {
  /*
   * X-Axis
   * */
  const xAxis = d3.axisBottom(xScaleGroup).tickFormat((date) => {
    if (selectedTimePeriod === CPITimePeriod.Week) {
      return formatXLabel({ date, selectedTimePeriod, selectedFrequency, filteredData })
    }
    if (selectedTimePeriod === CPITimePeriod.Month) {
      const groupedData = groupByWeeks(data)

      return formatXLabel({
        date,
        selectedTimePeriod,
        selectedFrequency,
        filteredData: groupedData.values,
        dataLastValue: filteredData[filteredData.length - 1]
      })
    }

    const groupedByWeeks = addStartEndWeekly(data)
    return formatXLabel({
      date,
      selectedTimePeriod,
      selectedFrequency,
      filteredData: groupedByWeeks.values
    })
  })

  const existingXAxis = svg.selectAll('.x-axis')
  if (!existingXAxis.empty()) existingXAxis.remove()
  const xAxisGroup = svg
    .append('g')
    .attr('class', 'x-axis')
    .attr('color', resolveThemeColor('text-primary', getActiveThemeMode()))
    .attr('transform', `translate(0, ${height + 19})`)
    .call(xAxis)

  styledXAxisGroup(xAxisGroup)
  xAxisGroup.selectAll('path').attr('display', 'none')
  const xAxisTick = svg.selectAll('.tick')
  xAxisTick.selectAll('line').attr('display', 'none')

  /*
   *  Y-Axis
   * */
  const yAxis = d3
    .axisRight(yScale)
    .ticks(5)
    .tickFormat((d, i) => {
      const unit = formatPerformanceUnit(healthUnit, true)

      return i === 0 ? '' : `${d}${unit}`
    })

  const existingYAxis = svg.selectAll('.y-axis')
  if (!existingYAxis.empty()) existingYAxis.remove()
  const yAxisGroup = svg.append('g').attr('class', 'y-axis').attr('color', 'none').call(yAxis)
  yAxisGroup
    .selectAll('text')
    .attr('dy', '18px')
    .attr('color', resolveThemeColor('text-primary', getActiveThemeMode()))
    .attr('font-size', theme.typography.fontSize.large)
    .attr('font-weight', theme.typography.fontWeight.bold)
    .attr('letter-spacing', '0.1px')
    .attr('line-height', '22px')
    .attr('font-family', theme.typography.fontFamily.primary)
  yAxisGroup.selectAll('path').attr('display', 'none')
  yAxisGroup.selectAll('line').attr('display', 'none')
}
